// src/pages/PaymentPage.js
import React, { useRef, useState } from "react";
import { useBooking } from "../context/BookingContext";
import { Box, Typography, Button, Stack } from "@mui/material";
import IGD_wordmark_green from "../assets/IGD_wordmark_green.png";
import { createCheckoutSession } from "../api/payment/payment";

function slotCountFromTime(time) {
  if (Array.isArray(time)) return time.length || 1;
  if (typeof time === "string") {
    const parts = time.split(",").map((s) => s.trim()).filter(Boolean);
    return parts.length || 1;
  }
  return 1;
}

export default function PaymentPage({ onBack }) {
  const { bookingDetails } = useBooking();

  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState(false);
  const startedRef = useRef(false);
  const abortRef = useRef(null);

  async function handlePayment() {
    if (startedRef.current) return;
    startedRef.current = true;
    setLoading(true);
    setOpening(true);

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const { url } = await createCheckoutSession({
        email: bookingDetails.email,
        date: bookingDetails.date,
        time: bookingDetails.time,
        duration: bookingDetails.duration,
        firstName: bookingDetails.firstName,
        lastName: bookingDetails.lastName,
        phone: bookingDetails.phone,
        attendees: bookingDetails.attendees,
        slotCount: slotCountFromTime(bookingDetails.time),
        signal: controller.signal,
      });

      window.location.href = url;
    } catch (err) {
      console.error("Error starting checkout:", err);
      alert(err?.message || "Failed to start checkout.");
    } finally {
      setLoading(false);
      setOpening(false);
      startedRef.current = false;
      abortRef.current = null;
    }
  }

  const handleBack = () => {
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch {}
    }

    if (typeof onBack === "function") {
      onBack();
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", px: 2, py: 4 }}>
      <Box textAlign="center" mb={4}>
        <Box
          component="img"
          src={IGD_wordmark_green}
          alt="Indy Golf Den"
          sx={{ height: 80, width: "auto" }}
        />
      </Box>

      <Typography
        variant="h4"
        align="center"
        gutterBottom
        color="#193e2d"
        sx={{ fontFamily: "lulo-one" }}
      >
        Complete Payment
      </Typography>

      <Typography align="center" paragraph>
        Your booking is almost complete.{" "}
        {opening
          ? "Opening Stripe Checkout…"
          : "Click below to proceed to secure payment."}
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={4}>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleBack}
          disabled={loading}
        >
          Back
        </Button>

        <Button
          fullWidth
          variant="contained"
          onClick={handlePayment}
          disabled={loading}
        >
          {loading ? "Preparing…" : "Proceed to Payment"}
        </Button>
      </Stack>
    </Box>
  );
}